// Common component overrides for both light and dark themes
// Uses centralized color palette from theme/index.js
export const componentOverrides = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        transition: 'background-color 0.3s ease, color 0.3s ease',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.sidebar.background,
        color: theme.palette.sidebar.text,
        border: 'none',
        boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.08)',
        marginTop: '10px',
        marginLeft: '10px',
        marginBottom: '10px',
        borderRadius: '16px',
        height: 'calc(100vh - 25px)',
        backgroundImage: 'none',
      }),
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.topbar.background,
        color: theme.palette.topbar.text,
        margin: {
          xs: '0',
          md: '10px 24px 0 24px',
        },
        marginTop: '10px',
        borderRadius: '10px',
        boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.06)',
        width: {
          xs: '100%',
          md: 'calc(100% - 48px)',
        },
        left: {
          xs: 0,
          md: '24px',
        },
        right: {
          xs: 0,
          md: '24px',
        },
        borderBottom: `1px solid ${theme.palette.topbar.border}`,
        backgroundImage: 'none',
      }),
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '12px',
        margin: '4px 8px',
        transition: 'all 0.2s ease',
        color: theme.palette.sidebar.menuItem.text,
        '&.SidebarMenuItem-active': {
          backgroundColor: theme.palette.sidebar.menuItem.backgroundActive,
          color: theme.palette.sidebar.menuItem.textActive,
          boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.08)',
          fontWeight: 500,
        },
        '&:hover': {
          backgroundColor: theme.palette.sidebar.menuItem.backgroundHover,
          boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.04)',
          transition: 'all 0.2s ease',
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '8px',
        textTransform: 'none',
        fontWeight: 500,
        transition: 'all 0.2s ease',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.15)',
        },
      }),
      contained: ({ theme }) => ({
        color: '#FFFFFF !important',
        '&:hover': {
          boxShadow: '0 6px 20px 0 rgba(0, 0, 0, 0.2)',
          color: '#FFFFFF !important',
        },
      }),
      containedPrimary: ({ theme }) => ({
        color: '#FFFFFF !important',
        backgroundColor: theme.palette.primary.main,
        '& .MuiSvgIcon-root': {
          color: '#FFFFFF !important',
        },
        '&:hover': {
          backgroundColor: theme.palette.primary.dark || '#CC5200',
          color: '#FFFFFF !important',
          '& .MuiSvgIcon-root': {
            color: '#FFFFFF !important',
          },
        },
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '12px',
        boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.08)',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.12)',
        },
      }),
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundImage: 'none',
      }),
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          '& fieldset': {
            borderColor: theme.palette.divider,
          },
          '&:hover fieldset': {
            borderColor: theme.palette.primary.main,
          },
        },
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '8px',
        fontWeight: 500,
      }),
      filled: ({ theme }) => ({
        backgroundColor: theme.palette.action.hover,
      }),
      filledPrimary: ({ theme }) => ({
        backgroundColor: theme.palette.primary.main,
        color: '#FFFFFF !important',
        '& .MuiChip-icon': {
          color: '#FFFFFF !important',
        },
        '& .MuiChip-label': {
          color: '#FFFFFF !important',
        },
      }),
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'light' ? '#f1f5f9' : '#334155',
        '& .MuiTableCell-head': {
          fontWeight: 600,
          color: theme.palette.text.primary,
          borderColor: theme.palette.divider,
        },
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: 'none',
        fontWeight: 500,
        minHeight: '48px',
        color: theme.palette.text.secondary,
        transition: 'all 0.2s ease',
        '&.Mui-selected': {
          color: theme.palette.primary.main,
          fontWeight: 600,
        },
      }),
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
}; 